import { selectedPartnerProductOrderCartItemsState } from "@/atoms/item/selected-partner-product-order-cart-items-state";
import { isOpenSameCartItemModal } from "@/atoms/quote/is-open-same-cart-item-modal";
import { toastState } from "@/atoms/system/toast-state";
import { profileState } from "@/atoms/user/profile-state";
import { ButtonColor } from "@/common/enum/button-color";
import { PartnerOrderPath } from "@/common/enum/path";
import { PaymentStatus } from "@/common/enum/payment-status";
import { formatCommasNumber } from "@/common/util/format-number-with-commas";
import { getShippingPriceText } from "@/common/util/get-shipping-price-text";
import { InvoicePDFDownloadButton } from "@/components/button/InvoicePDFDownloadButton";
import { OrderPDFDownloadButton } from "@/components/button/OrderPDFDownloadButton";
import { Body14Regular } from "@/components/typography/Body14Regular";
import { Body16Strong } from "@/components/typography/Body16Strong";
import { itemService } from "@/services/item-service";
import { orderService } from "@/services/order-service2";
import { ItemDetailLineDto } from "@/types/dto/item-detail-line-dto";
import { OrderDetailDto } from "@/types/dto/order-detail-dto";
import { Button, Table } from "flowbite-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";

interface Props {
  headersLength: number;
  data: OrderDetailDto;
}

export const PartnerOrderDetailItemTableFooter: React.FC<Props> = ({
  headersLength,
  data,
}) => {
  const navigate = useNavigate();
  const setToast = useSetRecoilState(toastState);
  const [profile, setProfile] = useRecoilState(profileState);
  const [cartItems, setCartItems] = useRecoilState(
    selectedPartnerProductOrderCartItemsState
  );
  const [modal, setModal] = useRecoilState(isOpenSameCartItemModal);

  if (!profile) {
    return null;
  }

  const paymentStatus = data.payment.status;

  function transformAggregateErrors(aggregateErrors: any) {
    return Object.entries(aggregateErrors).map(([key, message]) => ({
      id: Number(key),
      message: String(message),
    }));
  }

  const handleAddCartButtonClick = async () => {
    try {
      // const payload = data.cartItems.map((cartItem) => ({
      //   itemId: cartItem.item.id,
      //   quantity: cartItem.quantity,
      // }));

      const response = await orderService.validate(data.id);
      if (!response.ok && response.data.aggregateErrors) {
        setModal({
          cartItems: data.cartItems,
          errors: transformAggregateErrors(response.data.aggregateErrors),
        });
        return;
      }

      const cartItem = cartItems.find((item) =>
        data.cartItems.some((newItem) => item.childItem.id === newItem.item.id)
      );

      if (cartItem) {
        setCartItems(
          cartItems.map((item) => {
            const matchingItem = data.cartItems.find(
              (newItem) => item.childItem.id === newItem.item.id
            );

            return matchingItem
              ? {
                  ...item,
                  quantity: item.quantity + matchingItem.quantity,
                }
              : item;
          })
        );
      } else {
        // TODO 만약 아이템 하나가 안가져와져.. 어떻게 할까? 다음 태스크에 논의 필요
        const newItems = (
          await Promise.allSettled(
            data.cartItems.map(async (item) => {
              try {
                const response = await itemService.getById({
                  id: item.item.id,
                });
                if (response.ok) {
                  return {
                    childItem: response.data,
                    quantity: item.quantity,
                  };
                }
              } catch (error) {
                console.error(
                  `Failed to fetch item with id ${item.item.id}:`,
                  error
                );
              }
              return undefined;
            })
          )
        )

          .filter(
            (result): result is PromiseFulfilledResult<ItemDetailLineDto> =>
              result.status === "fulfilled" && result.value !== undefined
          )
          .map((result) => result.value);

        setCartItems([...cartItems, ...newItems]);
      }

      if (data.cartItems.length === 0) {
        alert("장바구니에 담을 수 있는 상품이 없습니다.");
        return;
      }

      navigate(PartnerOrderPath.ORDER);
      setToast({
        show: true,
        messages: ["같은 상품을 장바구니에", "담았습니다."],
      });
    } catch (error) {
      console.log(error);
    }

    // const cartItem = cartItems.find((item) =>
    //   data.cartItems.some((dataItem) => item.childItem.id === dataItem.id)
    // );

    // if (cartItem) {
    //   setCartItems(
    //     cartItems.map((item) => {
    //       const matchingDataItem = data.cartItems.find(
    //         (dataItem) => item.childItem.id === dataItem.id
    //       );
    //       return matchingDataItem
    //         ? {
    //             ...item,
    //             quantity: item.quantity + matchingDataItem.quantity,
    //           }
    //         : item;
    //     })
    //   );
    // } else {
    //   // setCartItems([
    //   //   ...cartItems,
    //   //   ...data.cartItems.map((dataItem) => ({
    //   //     childItem: dataItem.childItem,
    //   //     quantity: dataItem.quantity,
    //   //   })),
    //   // ]);
    // }
  };

  return (
    <Table.Row className="bg-white">
      <Table.Cell colSpan={headersLength}>
        <div className="flex justify-between p-[16px] items-center">
          <div className="flex gap-[8px]">
            <OrderPDFDownloadButton orderDetail={data} profile={profile}>
              <Button size="sm" color={ButtonColor.TRANSPARENT_OUTLINE}>
                발주서 저장
              </Button>
            </OrderPDFDownloadButton>
            {paymentStatus === PaymentStatus.COMPLETED && (
              <InvoicePDFDownloadButton orderDetail={data}>
                <Button size="sm" color={ButtonColor.TRANSPARENT_OUTLINE}>
                  거래명세표 저장
                </Button>
              </InvoicePDFDownloadButton>
            )}
            <div onClick={handleAddCartButtonClick}>
              <Button size="sm" color={ButtonColor.TRANSPARENT_OUTLINE}>
                같은 상품 장바구니 담기
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-[2px]  items-end">
            <Body14Regular className="text-semantic-text-tertiary">
              {`정가 ${formatCommasNumber(
                data.originalPriceSum
              )} - 기본 할인 ${formatCommasNumber(
                data.discountPriceSum
              )} - 인센티브 할인 ${formatCommasNumber(
                data.incentiveDiscountPriceSum
              )} + 배송비 ${getShippingPriceText(data.shippingPrice)}`}
            </Body14Regular>
            <Body16Strong className="text-semantic-text-body">
              {`총 금액 (VAT 포함) ${formatCommasNumber(data.finalPriceSum)}원`}
            </Body16Strong>
          </div>
        </div>
      </Table.Cell>
    </Table.Row>
  );
};
